import { fetch, buildUrl } from 'domain/Api';
import { ApiCall } from 'omni-common-ui';
import is from 'is_js';
import { RAV2_COURSE_TYPE_LEVEL_IDS } from 'domain/CourseType';

export const FETCH_GROUP_REQUEST = 'FETCH_GROUP_REQUEST';
export const FETCH_GROUP_SUCCESS = 'FETCH_GROUP_SUCCESS';
export const FETCH_GROUP_FAILURE = 'FETCH_GROUP_FAILURE';
export const SYNC_GROUP_REQUEST = 'SYNC_GROUP_REQUEST';
export const SYNC_GROUP_SUCCESS = 'SYNC_GROUP_SUCCESS';
export const SYNC_GROUP_FAILURE = 'SYNC_GROUP_FAILURE';

export function getGroup(state, groupId) {
  return state.get('rootReducer').get('groups').get(groupId) &&
    state.get('rootReducer').get('groups').get(groupId).data;
}

export function getCentre(state, groupId) {
  const group = state.get('rootReducer').get('groups').get(groupId);
  if (! group || ! group.data) return {};
  return state.get('rootReducer').get('centres').get(group.data.centreId) || {};
}

export function isCNGroup(state, groupId) {
  const centre = getCentre(state, groupId);
  return ! is.undefined(centre.countryCode) && centre.countryCode.toUpperCase() === 'CN';
}

export function isIDGroup(state, groupId) {
  const centre = getCentre(state, groupId);
  return ! is.undefined(centre.countryCode) && centre.countryCode.toUpperCase() === 'ID';
}

export function isGlobalGroup(state, groupId) {
  const centre = getCentre(state, groupId);
  if (is.undefined(centre.countryCode)) return false;
  return centre.countryCode.toUpperCase() === 'RU' || centre.countryCode.toUpperCase() === 'ID';
}

export function isTBCourseTypeGroup(state, groupId) {
  return checkCurrentGroupCourseType(state, groupId, 'TB');
}

export function isTBv3Group(state, groupId) {
  return checkCurrentGroupCourseTypeAndVersion(state, groupId, 'TB', 'v3');
}

export function isNotTBv3Group(state, groupId) {
  return checkCurrentGroupCourseType(state, groupId, 'TB') &&
    ! checkCurrentGroupCourseTypeAndVersion(state, groupId, 'TB', 'v3');
}

export function isHFCourseTypeGroup(state, groupId) {
  return checkCurrentGroupCourseType(state, groupId, 'HF');
}

export function isHFV4CourseTypeGroup(state, groupId) {
  return checkCurrentGroupCourseTypeAndVersion(state, groupId, 'HF', 'V4');
}

export function isSSCourseTypeGroup(state, groupId) {
  return checkCurrentGroupCourseType(state, groupId, 'SS');
}

export function isSSV3Group(state, groupId) {
  return checkCurrentGroupCourseTypeAndVersion(state, groupId, 'SS', 'V3');
}

export function hasPaperBasedHomework(state, groupId) {
  return (isHFCourseTypeGroup(state, groupId) && ! isHFV4CourseTypeGroup(state, groupId)) ||
    isSSCourseTypeGroup(state, groupId);
}

export function isFRCourseTypeGroup(state, groupId) {
  return checkCurrentGroupCourseType(state, groupId, 'FR');
}

export function isFRV2CourseTypeGroup(state, groupId) {
  return checkCurrentGroupCourseType(state, groupId, 'FR') &&
    checkCurrentGroupCourseTypeAndVersion(state, groupId, 'FR', 'v2');
}

export function isDisabledReportActions(state, groupId) {
  return isCNGroup(state, groupId) &&
    (isTBCourseTypeGroup(state, groupId) ||
      isSSCourseTypeGroup(state, groupId) ||
      (isHFCourseTypeGroup(state, groupId) && ! isHFV4CourseTypeGroup(state, groupId)));
}

export function isSupportPTExportActions(state, groupId) {
  return isCNGroup(state, groupId) &&
    (isTBv3Group(state, groupId) ||
      (isHFCourseTypeGroup(state, groupId) && ! isHFV4CourseTypeGroup(state, groupId)));
}

export function isSupportPTRetaken(state, groupId) {
  return isTBv3Group(state, groupId) ||
  (isHFCourseTypeGroup(state, groupId) && ! isHFV4CourseTypeGroup(state, groupId)) ||
    isFRCourseTypeGroup(state, groupId);
}

export function isSupportStudyReport(state, groupId) {
  return isTBv3Group(state, groupId) ||
    isFRCourseTypeGroup(state, groupId) ||
    (isHFCourseTypeGroup(state, groupId) &&
      ! isHFV4CourseTypeGroup(state, groupId) &&
      hasContentPath(state, groupId));
}

export function isSupportUnitReport(state, groupId) {
  return (isSSCourseTypeGroup(state, groupId) ||
    isTBv3Group(state, groupId) ||
    (isHFCourseTypeGroup(state, groupId) && ! isHFV4CourseTypeGroup(state, groupId)) ||
    isFRCourseTypeGroup(state, groupId)) &&
    ! isSupportStudyReport(state, groupId);
}

export function isGrammarProGroup(state, groupId) {
  return checkCurrentGroupCourseType(state, groupId, 'GP');
}

export function isKETCourseTypeGroup(state, groupId) {
  return checkCurrentGroupCourseType(state, groupId, 'KET');
}

export function isRAv2CourseTypeGroup(state, groupId) {
  const group = state.get('rootReducer').get('groups').get(groupId);
  if (! group || ! group.data || ! group.data.courseTypeLevelId) return false;
  return RAV2_COURSE_TYPE_LEVEL_IDS.includes(group.data.courseTypeLevelId);
}

export function hasContentPath(state, groupId) {
  const group = state.get('rootReducer').get('groups').get(groupId);
  if (! group || ! group.data) return;
  return !! group.data.hasContentPath;
}

function checkCurrentGroupCourseType(state, groupId, courseTypeCode) {
  const group = state.get('rootReducer').get('groups').get(groupId);
  if (! group || ! group.data || ! group.data.courseTypeCode) return;
  return group.data.courseTypeCode.toLowerCase() === courseTypeCode.toLowerCase();
}

function checkCurrentGroupCourseTypeAndVersion(state, groupId, courseTypeCode, version) {
  const group = state.get('rootReducer').get('groups').get(groupId);
  if (! group || ! group.data ||
    ! group.data.courseTypeCode || ! group.data.courseTypeLevelCode) {
    return;
  }
  return group.data.courseTypeCode.toLowerCase() === courseTypeCode.toLowerCase() &&
    group.data.courseTypeLevelCode.toLowerCase().startsWith(version.toLowerCase());
}

export function shouldEnableMarkAttendance(state, groupId, defaultValue = false) {
  const shouldEnable = isFromOdin(state, groupId);
  if (is.undefined(shouldEnable)) return defaultValue;
  return !! shouldEnable;
}

export function shouldShowAch(state, groupId) {
  const shouldShow = isFromOdin(state, groupId);
  if (is.undefined(shouldShow)) return true;
  return !! shouldShow;
}

function isFromOdin(state, groupId) {
  const group = state.get('rootReducer').get('groups').get(groupId);
  if (! group || ! group.data) return;
  return !! group.data.isFromOdin;
}

export function isAutoPublishHomework(state, groupId) {
  const group = state.get('rootReducer').get('groups').get(groupId);
  if (! group || ! group.data) return;
  return group.data.autoPublishContent &&
    group.data.autoPublishContent.includes('Assign Homework');
}

export function isAutoPublishCIC(state, groupId) {
  const group = state.get('rootReducer').get('groups').get(groupId);
  if (! group || ! group.data) return;
  return group.data.autoPublishContent && group.data.autoPublishContent.includes('CiC');
}

const buildGroupUrl = (groupId) => buildUrl(`/groups/${groupId}`);
const method = 'GET';

export function fetchGroupIfNeeded(groupId) {
  return (dispatch, getState) => {
    const state = getState();
    if (! ApiCall.shouldPerform(state, { method, url: buildGroupUrl(groupId) })) {
      return false;
    }

    return dispatch(fetchGroup(groupId));
  };
}

export function fetchGroup(id) {
  return (dispatch) => {
    const url = buildGroupUrl(id);
    return dispatch(fetchGroupRequest(id)).payload
      .then((response) => dispatch(fetchGroupSuccess(response)))
      .catch((error) => dispatch(fetchGroupFailure(error)));

    function fetchGroupRequest() {
      return ApiCall.createAction({
        type: FETCH_GROUP_REQUEST,
        payload: fetch(url),
        url,
        method,
        id,
      });
    }

    function fetchGroupSuccess(group) {
      return ApiCall.createAction({
        type: FETCH_GROUP_SUCCESS,
        payload: group,
        url,
        method,
        id,
      });
    }

    function fetchGroupFailure(error) {
      return ApiCall.createAction({
        type: FETCH_GROUP_FAILURE,
        error,
        url,
        method,
        id,
      });
    }
  };
}

export function syncGroupInfo(id) {
  return (dispatch) => {
    const url = buildUrl(`/groups/${id}/refresh`);

    return dispatch(syncGroupRequest()).payload
      .then((response) => dispatch(syncGroupSuccess(response)))
      .catch((error) => dispatch(syncGroupFailure(error)));

    function syncGroupRequest() {
      return ApiCall.createAction({
        type: SYNC_GROUP_REQUEST,
        payload: fetch(url),
        url,
        method,
      });
    }

    function syncGroupSuccess(response) {
      return ApiCall.createAction({
        type: SYNC_GROUP_SUCCESS,
        payload: response,
        url,
        method,
      });
    }

    function syncGroupFailure(error) {
      return ApiCall.createAction({
        type: SYNC_GROUP_FAILURE,
        error,
        url,
        method,
      });
    }
  };
}



// WEBPACK FOOTER //
// ./containers/Group/actions.js